// Bench Candidates API client (ADR-107).

import {
  authenticatedApi,
  ApiError,
  API_BASE_URL,
} from './apiUtils';

const BASE = `${API_BASE_URL}/bench-candidates`;

const buildQuery = (params = {}) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return;
      }
      sp.append(key, value.join(','));
    } else {
      sp.append(key, value);
    }
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
};

// radar-api returns { error, code, reasons?, requestId? } on 4xx — keep the
// structured parts so the page can show eligibility reasons verbatim.
const wrap = (fallback, error) => new ApiError(error?.data?.error || fallback, error?.status || 500, error?.data);

export const benchCandidatesApi = {
  /**
   * List candidates (cursor pagination). See radar-api benchCandidate.service#listCandidates for filters.
   */
  list: async (filters = {}) => {
    try {
      return await authenticatedApi.get(`${BASE}${buildQuery(filters)}`);
    } catch (error) {
      throw wrap('Failed to list bench candidates', error);
    }
  },

  /** Single candidate with events, import requests and assignPrefill. */
  get: async (candidateId) => {
    try {
      return await authenticatedApi.get(`${BASE}/${candidateId}`);
    } catch (error) {
      throw wrap('Failed to fetch bench candidate', error);
    }
  },

  /** Assign to a bench-purpose project (engagement type is fixed server-side to PT). */
  assignToBench: async (candidateId, body) => {
    try {
      return await authenticatedApi.post(`${BASE}/${candidateId}/assign-to-bench`, body);
    } catch (error) {
      throw wrap('Failed to assign candidate to bench', error);
    }
  },

  cancelLeave: async (candidateId) => {
    try {
      return await authenticatedApi.post(`${BASE}/${candidateId}/cancel-leave`, {});
    } catch (error) {
      throw wrap('Failed to cancel bench leave', error);
    }
  },

  unassign: async (candidateId, body = {}) => {
    try {
      return await authenticatedApi.post(`${BASE}/${candidateId}/unassign`, body);
    } catch (error) {
      throw wrap('Failed to remove candidate from bench', error);
    }
  },

  dismiss: async (candidateId, reason) => {
    try {
      return await authenticatedApi.post(`${BASE}/${candidateId}/dismiss`, { reason });
    } catch (error) {
      throw wrap('Failed to dismiss candidate', error);
    }
  },

  undismiss: async (candidateId) => {
    try {
      return await authenticatedApi.post(`${BASE}/${candidateId}/undismiss`, {});
    } catch (error) {
      throw wrap('Failed to undismiss candidate', error);
    }
  },

  /** 202 — the import runs on radar-scheduler; poll the row. */
  requestImport: async (candidateId) => {
    try {
      return await authenticatedApi.post(`${BASE}/${candidateId}/import`, {});
    } catch (error) {
      throw wrap('Failed to request import', error);
    }
  },

  listRuns: async ({ sourceSystem, status, limit = 20, cursor } = {}) => {
    try {
      return await authenticatedApi.get(`${BASE}/sync-runs${buildQuery({ sourceSystem, status, limit, cursor })}`);
    } catch (error) {
      throw wrap('Failed to list bench candidate sync runs', error);
    }
  },

  getLatestRun: async (sourceSystem) => {
    try {
      return await authenticatedApi.get(`${BASE}/sync-runs/latest${buildQuery({ sourceSystem })}`);
    } catch (error) {
      throw wrap('Failed to fetch latest bench candidate sync run', error);
    }
  },

  /** "Refresh now" — proxied to radar-scheduler; 202. */
  triggerRefresh: async (sourceSystem) => {
    try {
      return await authenticatedApi.post(`${BASE}/sync-runs/trigger-now`, { sourceSystem });
    } catch (error) {
      throw wrap('Failed to trigger refresh', error);
    }
  },
};
